import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps keyboard focus inside one element while it is open.
 *
 * `Sheet` gets this for free from `<dialog>`. `Panel` and `Drawer` do not use `<dialog>`, because
 * a modal dialog paints over the whole viewport and the panel is meant to sit beside the page it
 * belongs to at desktop width. So the three things the browser would otherwise do are done here:
 * focus moves in on open, Tab wraps at both ends, and focus goes back to where it was on close.
 * Escape is the caller's, through `onEscape`, so a panel that is mid-save can choose to ignore it.
 *
 * `onEscape` and `returnTo` are read through refs, so a new function on each render does not run
 * the effect again. A run again moves focus out of the field and back to the container, and on an
 * iPad that closes the keyboard: the keyboard resizes the page, the page renders again, the field
 * loses focus, and nobody can type.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape?: () => void,
  /** Where focus goes on close. Defaults to whatever was focused when the trap opened. */
  returnTo?: RefObject<HTMLElement | null>,
): void {
  const escape = useRef(onEscape);
  const back = useRef(returnTo);
  useLayoutEffect(() => {
    escape.current = onEscape;
    back.current = returnTo;
  });

  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    container.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        escape.current?.();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const current = document.activeElement;

      if (event.shiftKey && (current === first || current === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      // The return target as it is now, at close: a caller can change it while the trap is open.
      (returnTarget(back) ?? opener)?.focus();
    };
  }, [ref, active]);
}

/**
 * The element a `returnTo` ref points at now. Cleanup reads it at close on purpose (a caller can
 * move the target while the trap is open), through this function so the hooks lint does not take
 * it for the usual stale-ref mistake.
 */
function returnTarget(box: RefObject<RefObject<HTMLElement | null> | undefined>): HTMLElement | null {
  return box.current?.current ?? null;
}
